import { useId } from 'react';
import { useIntl } from 'react-intl';
import InfoTip from '../../components/novarium/InfoTip';
import { NewBornGranularity, NewBornTimelinePointDTO } from '../../dto/newborn/NewBornDTO';
import { PeakAnalysis } from '../ServicesStatistics/peaks';
import { formatCount, formatPercent } from '../UrgencySummary/urgencySummaryFormat';
import { periodTitleOf } from './newBornPeriodText';

interface NewBornPeakChipsProps {
    points: NewBornTimelinePointDTO[];
    granularity: NewBornGranularity;
    analysis: PeakAnalysis;
    /** Abre el panel del período; `opener` es el chip, a donde vuelve el foco al cerrar el panel. */
    onOpen: (point: NewBornTimelinePointDTO, opener: HTMLElement) => void;
}

/**
 * «Picos»: un botón por cada pico del % de cesáreas marcado en el gráfico («Semana del 14/09 al 20/09 · 93,8 % · 15 de 16
 * partos ›»), la entrada más fácil con el dedo y con lector de pantalla. Abre el mismo panel que el click en el gráfico. Sin
 * picos no se muestra nada. El ⓘ dice el criterio, con la cantidad mínima de partos que usó el cálculo.
 */
export default function NewBornPeakChips({ points, granularity, analysis, onOpen }: NewBornPeakChipsProps) {
    const intl = useIntl();
    const labelId = useId();
    if (analysis.peaks.length === 0) return null;

    return (
        <div className="nv-newborn-peaks">
            <span className="nv-newborn-peaks__label">
                <span className="nv-strong" id={labelId}>{intl.formatMessage({ id: 'NewBornPeaksTitle' })}</span>
                <InfoTip text={intl.formatMessage({ id: 'NewBornPeaksCriteria' }, { min: formatCount(intl, analysis.minAttentions) })} />
            </span>
            <ul className="nv-newborn-peaks__list" aria-labelledby={labelId}>
                {analysis.peaks.map((index) => {
                    const point = points[index];
                    return (
                        <li key={point.period}>
                            <button type="button" className="nv-newborn-peak-chip" onClick={(event) => onOpen(point, event.currentTarget)}>
                                <span>
                                    {intl.formatMessage({ id: 'NewBornPeaksChip' }, {
                                        period: periodTitleOf(intl, point, granularity),
                                        value: formatPercent(intl, point.cesareanPct),
                                        deliveries: intl.formatMessage({ id: 'NewBornKpiOfDeliveries' }, {
                                            count: formatCount(intl, point.cesareans), total: formatCount(intl, point.cesareanBase),
                                        }),
                                    })}
                                </span>
                                <i className="pi pi-angle-right" aria-hidden="true" />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
